"use client";

// Last-resort boundary: catches failures in the root layout itself, where
// app/error.tsx cannot help because the layout (and so the site header, fonts
// and stylesheet) never rendered. It must supply its own <html>/<body>, so the
// markup here is deliberately self-contained and inline-styled — no imports
// that could themselves be the thing that failed.
import { useEffect } from "react";
import { reportError } from "@/lib/reportError";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError({
      kind: "render",
      message: error.message || "Root layout failed to render",
      source: error.digest ? `global digest:${error.digest}` : "global",
      stack: error.stack ?? null,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fcffff",
          color: "#303030",
          fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
          padding: "2rem",
        }}
      >
        <div style={{ maxWidth: "34rem", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: "0.75rem", letterSpacing: "0.28em", textTransform: "uppercase", color: "#386963" }}>
            Something went wrong
          </p>
          <h1 style={{ margin: "1.25rem 0 0", fontSize: "2rem", fontWeight: 500, lineHeight: 1.13 }}>
            This page didn&rsquo;t load
          </h1>
          <p style={{ margin: "1.25rem 0 0", lineHeight: 1.6, color: "#434243" }}>
            Sorry — something on our side failed. The issue has been logged for
            our team. Please try again, or return to the homepage.
          </p>
          <div style={{ marginTop: "2rem", display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={reset}
              style={{ background: "#447973", color: "#fff", border: 0, borderRadius: 999, padding: "0.85rem 1.75rem", fontSize: "0.95rem", cursor: "pointer" }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{ border: "1.5px solid #447973", color: "#447973", borderRadius: 999, padding: "0.85rem 1.75rem", fontSize: "0.95rem", textDecoration: "none" }}
            >
              Go to homepage
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
